import type {
  CeilingMaterialName,
  FloorMaterialName,
  Material,
  MaterialCategory,
  SurfaceMaterialName,
  WallMaterialName,
} from '@/types/material';

/**
 * Material library. Values ported 1:1 from the legacy index.html so the
 * React rewrite produces bit-identical RT60 numbers against the v1 build.
 *
 * Absorption coefficients are peer-reviewed average values taken from
 * standard references (Bobran, Harris, Cox & D'Antonio). They are the
 * same set shipped in the original Omma.build submission.
 */

/* ─── Walls ────────────────────────────────────────────────────────── */

const WALL_MATERIALS: Record<WallMaterialName, Material> = {
  Drywall: {
    name: 'Drywall',
    category: 'walls',
    alpha: [0.29, 0.1, 0.05, 0.04, 0.07, 0.09],
    desc: 'Standard gypsum board',
    three: { color: 0xd4ccb8, emissive: 0x1a1810, roughness: 0.85, metalness: 0.0 },
    swatch: '#c8c0a8',
  },
  Concrete: {
    name: 'Concrete',
    category: 'walls',
    alpha: [0.01, 0.01, 0.02, 0.02, 0.02, 0.02],
    desc: 'Bare concrete surface',
    three: { color: 0x8a8a8a, emissive: 0x0a0a0a, roughness: 0.95, metalness: 0.05 },
    swatch: '#888888',
  },
  Brick: {
    name: 'Brick',
    category: 'walls',
    alpha: [0.03, 0.03, 0.03, 0.04, 0.05, 0.07],
    desc: 'Exposed brick wall',
    three: { color: 0x8b4a3a, emissive: 0x100808, roughness: 0.9, metalness: 0.0 },
    swatch: '#8b4a3a',
  },
  'Wood panels': {
    name: 'Wood panels',
    category: 'walls',
    alpha: [0.15, 0.11, 0.1, 0.07, 0.06, 0.07],
    desc: 'Wooden panel cladding',
    three: { color: 0x7a5c3a, emissive: 0x0f0a05, roughness: 0.8, metalness: 0.05 },
    swatch: '#7a5c3a',
  },
};

/* ─── Floors ───────────────────────────────────────────────────────── */

const FLOOR_MATERIALS: Record<FloorMaterialName, Material> = {
  Concrete: {
    name: 'Concrete',
    category: 'floors',
    alpha: [0.01, 0.01, 0.02, 0.02, 0.02, 0.02],
    desc: 'Polished concrete',
    three: { color: 0x808080, emissive: 0x080808, roughness: 0.95, metalness: 0.05 },
    swatch: '#808080',
  },
  Wood: {
    name: 'Wood',
    category: 'floors',
    alpha: [0.15, 0.11, 0.1, 0.07, 0.06, 0.07],
    desc: 'Hardwood flooring',
    three: { color: 0x8c6240, emissive: 0x100a04, roughness: 0.75, metalness: 0.05 },
    swatch: '#8c6240',
  },
  'Carpet (thick)': {
    name: 'Carpet (thick)',
    category: 'floors',
    alpha: [0.02, 0.06, 0.14, 0.37, 0.6, 0.65],
    desc: 'Thick carpet on concrete',
    three: { color: 0x3a3250, emissive: 0x080610, roughness: 1.0, metalness: 0.0 },
    swatch: '#3a3250',
  },
  'Carpet + pad': {
    name: 'Carpet + pad',
    category: 'floors',
    alpha: [0.08, 0.24, 0.57, 0.69, 0.71, 0.73],
    desc: 'Thick carpet with pad',
    three: { color: 0x4a3d5c, emissive: 0x090710, roughness: 1.0, metalness: 0.0 },
    swatch: '#4a3d5c',
  },
  'Vinyl/linoleum': {
    name: 'Vinyl/linoleum',
    category: 'floors',
    alpha: [0.02, 0.03, 0.03, 0.03, 0.03, 0.02],
    desc: 'Smooth vinyl sheet',
    three: { color: 0xb0a898, emissive: 0x0c0b0a, roughness: 0.3, metalness: 0.1 },
    swatch: '#b0a898',
  },
};

/* ─── Ceilings ─────────────────────────────────────────────────────── */

const CEILING_MATERIALS: Record<CeilingMaterialName, Material> = {
  Drywall: {
    name: 'Drywall',
    category: 'ceilings',
    alpha: [0.29, 0.1, 0.05, 0.04, 0.07, 0.09],
    desc: 'Standard gypsum board',
    three: { color: 0xd4ccb8, emissive: 0x1a1810, roughness: 0.85, metalness: 0.0 },
    swatch: '#c8c0a8',
  },
  Concrete: {
    name: 'Concrete',
    category: 'ceilings',
    alpha: [0.01, 0.01, 0.02, 0.02, 0.02, 0.02],
    desc: 'Bare concrete ceiling',
    three: { color: 0x8a8a8a, emissive: 0x0a0a0a, roughness: 0.95, metalness: 0.05 },
    swatch: '#888888',
  },
  'Acoustic tile': {
    name: 'Acoustic tile',
    category: 'ceilings',
    alpha: [0.58, 0.91, 0.78, 1.01, 1.05, 1.13],
    desc: 'Dropped acoustic tiles',
    three: { color: 0x4a4a52, emissive: 0x060608, roughness: 1.0, metalness: 0.0 },
    swatch: '#4a4a52',
  },
};

/* ─── Indexes ──────────────────────────────────────────────────────── */

/** All materials grouped by the surface category they dress. */
export const MATERIALS_BY_CATEGORY = {
  walls: WALL_MATERIALS,
  floors: FLOOR_MATERIALS,
  ceilings: CEILING_MATERIALS,
} as const;

/** Flat list of every material (for iteration / search). */
export const ALL_MATERIALS: readonly Material[] = [
  ...Object.values(WALL_MATERIALS),
  ...Object.values(FLOOR_MATERIALS),
  ...Object.values(CEILING_MATERIALS),
];

/* ─── Lookup helpers ───────────────────────────────────────────────── */

/**
 * Resolve a material by category + name. Returns `undefined` when the
 * combination is invalid (e.g. asking for "Acoustic tile" in `floors`).
 */
export function getMaterial(
  category: MaterialCategory,
  name: SurfaceMaterialName,
): Material | undefined {
  const bucket = MATERIALS_BY_CATEGORY[category] as Record<string, Material>;
  return bucket[name];
}

/** Convenience: swatch color for any material name across any category. */
export function getSwatchColor(name: SurfaceMaterialName): string {
  const hit = ALL_MATERIALS.find((m) => m.name === name);
  return hit?.swatch ?? '#888';
}

/** Names of materials available for a given surface category. */
export function listMaterialNames(category: MaterialCategory): SurfaceMaterialName[] {
  return Object.keys(MATERIALS_BY_CATEGORY[category]) as SurfaceMaterialName[];
}
